import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchSearchPatientList } from "../../../../redux/slices/patientlookup.slice";
import { getFacilitiesDetails } from "../../../../api/list";
import { PatientSearchState, PatientSearchActions } from "../types";

interface UsePatientSearchReturn {
  state: PatientSearchState;
  actions: PatientSearchActions;
}

const usePatientSearch = (): UsePatientSearchReturn => {
  const dispatch: any = useDispatch();

  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [showSearch, setShowSearch] = useState<string>("");
  const [facilities, setFacilities] = useState<any>([]);
  const [selectedFacility, setSelectedFacility] = useState<string>("");
  const [searchDob, setSearchDob] = useState<string>("");
  const [patientSearchLoader, setPatientSearchLoader] = useState<boolean>(false);
  const [patientResults, setPatientResults] = useState<any>([]);
  const [patientPage, setPatientPage] = useState<number>(1);
  const [patientTotalPages, setPatientTotalPages] = useState<number>(1);
  const [patientTotalCount, setPatientTotalCount] = useState<number>(0);
  const [searchInitiated, setSearchInitiated] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");

  // Fetch facilities on mount
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const result = await getFacilitiesDetails();
        if (result?.result) {
          setFacilities(result?.result?.data || []);
        }
      } catch (error) {
        console.error("Error fetching facilities:", error);
      }
    };
    fetchFacilities();
  }, []);

  const handleFetchPatients = (pageNumber?: number) => {
    const pg = pageNumber || patientPage;
    if (pg === 1) {
      setPatientSearchLoader(true);
      setSearchError("");
    }

    dispatch(
      fetchSearchPatientList({
        page: pg,
        limit: 15,
        sort_by: "lastName",
        sort_dir: "asc",
        name: showSearch,
        fac_id: selectedFacility,
        DOB: searchDob || undefined,
      })
    )
      .then((res: any) => {
        if (res?.error) {
          setSearchError("Failed to search patients. Please try again.");
          setPatientResults([]);
          setPatientSearchLoader(false);
          return;
        }

        const data = res?.payload?.data || [];
        const totalCount = res?.payload?.total_count || 0;

        if (pg === 1) {
          setPatientResults(data);
          if (totalCount === 1 && data.length === 1) {
            setSelectedPatient(data[0]);
          }
        } else {
          setPatientResults((prev: any) => [...prev, ...data]);
        }
        setPatientTotalPages(res?.payload?.total_pages || 1);
        setPatientTotalCount(res?.payload?.total_count || 0);
        setPatientSearchLoader(false);
      })
      .catch(() => {
        setSearchError("Failed to search patients. Please try again.");
        setPatientResults([]);
        setPatientSearchLoader(false);
      });
  };

  const handleSearchClick = () => {
    if (!showSearch.trim() && !selectedFacility && !searchDob) {
      setSearchError(
        "Please enter a name, MRN, select a location, or date of birth to search."
      );
      return;
    }
    setSearchError("");
    setSearchInitiated(true);
    setSelectedPatient(null);
    setPatientPage(1);
    handleFetchPatients(1);
  };

  const handleNextPatientPage = () => {
    const nextPage = patientPage + 1;
    setPatientPage(nextPage);
    handleFetchPatients(nextPage);
  };

  const handleClearFilters = () => {
    setShowSearch("");
    setSelectedFacility("");
    setSearchDob("");
    setSearchInitiated(false);
    setSelectedPatient(null);
    setPatientResults([]);
    setSearchError("");
  };

  const clearSearchError = () => {
    setSearchError("");
  };

  return {
    state: {
      showSearch,
      selectedFacility,
      searchDob,
      patientSearchLoader,
      patientResults,
      patientPage,
      patientTotalPages,
      patientTotalCount,
      searchInitiated,
      searchError,
      selectedPatient,
      facilities,
    },
    actions: {
      setShowSearch,
      setSelectedFacility,
      setSearchDob,
      setSelectedPatient,
      clearSearchError,
      handleSearchClick,
      handleNextPatientPage,
      handleClearFilters,
    },
  };
};

export default usePatientSearch;